// Routes (A11): code-based TanStack Router — typed without a codegen step. The global "filtrar por titular" control is
// the root search param `titular` (an AnmEntity id), validated here and carried across every navigation. Screens are
// lazy chunks.
import {
  createRootRoute, createRoute, createRouter, lazyRouteComponent, parseSearchWith, redirect, retainSearchParams, stringifySearchWith, type RouterHistory,
} from '@tanstack/react-router';
import type { ComponentType } from 'react';
import { z } from 'zod';
import { ErrorScreen, NotFound } from './components/ErrorScreen.tsx';
import { Shell } from './components/Shell.tsx';

// Ids in URLs are the API's public ids (UUIDs, Plan 6 Task 6b) — a numeric `?titular=` from before is dropped.
const PublicId = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
export const RootSearch = z.object({ titular: PublicId.optional().catch(undefined) });
export type RootSearch = z.infer<typeof RootSearch>;

export const rootRoute = createRootRoute({
  validateSearch: RootSearch,
  search: { middlewares: [retainSearchParams(['titular'])] },
  component: Shell,
  errorComponent: ErrorScreen,
  notFoundComponent: NotFound,
});

const asString = (v: unknown) => (v == null ? undefined : String(v));
const screen = <P extends string>(path: P, load: () => Promise<{ default: ComponentType }>) =>
  createRoute({ getParentRoute: () => rootRoute, path, component: lazyRouteComponent(load) });

// Plan 4b screens (artboards in design/project)
// Plan 7 (navigation redesign): each screen's tab lives in the URL (`?aba=`), so an alert or a link opens the right one.
const tabOf = <T extends readonly [string, ...string[]]>(tabs: T) => (raw: Record<string, unknown>) =>
  z.object({ aba: z.enum(tabs).optional().catch(undefined) }).parse({ aba: asString(raw.aba) });
export const ATIVIDADES_TABS = ['geral', 'prazos', 'movimentacoes'] as const;
export const PROCESSOS_TABS = ['minerarios', 'ambientais', 'sei'] as const;
export const PROCESSO_TABS = ['linha', 'anm', 'ambiental', 'territorio', 'sei', 'financeiro'] as const;
export const TERRITORIO_TABS = ['superficiarios', 'sobreposicao', 'oportunidades'] as const;
// Atividades (replaces Hoje + Prazos): deadlines and the movements across the processes
const atividades = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  validateSearch: tabOf(ATIVIDADES_TABS),
  component: lazyRouteComponent(() => import('./screens/Atividades.tsx')),
});
// Processos: every process of the visible titulares (list or map), their agency licences, their SEI administrative processes
const processos = createRoute({
  getParentRoute: () => rootRoute,
  path: '/processos',
  validateSearch: (raw: Record<string, unknown>) => ({
    ...tabOf(PROCESSOS_TABS)(raw),
    ...z.object({ vista: z.enum(['lista', 'mapa']).optional().catch(undefined) }).parse({ vista: asString(raw.vista) }),
  }),
  component: lazyRouteComponent(() => import('./screens/Processos.tsx')),
});
const processo = createRoute({
  getParentRoute: () => rootRoute,
  path: '/processos/$processoId',
  validateSearch: tabOf(PROCESSO_TABS),
  component: lazyRouteComponent(() => import('./screens/Processo.tsx')),
});
// Território: superficiários over the portfolio, the overlap check and the areas in disponibilidade
const territorio = createRoute({
  getParentRoute: () => rootRoute,
  path: '/territorio',
  validateSearch: tabOf(TERRITORIO_TABS),
  component: lazyRouteComponent(() => import('./screens/Territorio.tsx')),
});
// SEI (Plan 6 Task 7, SEI module): the titulares' processes on SEI, and one of them (where the SEI alerts link)
// Plan 7: the screens these URLs held are tabs now — old links and alert e-mails land on the tab, the titular kept
const moved = <P extends string>(path: P, to: '/' | '/processos' | '/territorio', aba: string) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    beforeLoad: ({ search }) => {
      throw redirect({ to, search: { titular: (search as { titular?: string }).titular, aba } as never, replace: true });
    },
  });
const prazos = moved('/prazos', '/', 'prazos');
const sei = moved('/sei', '/processos', 'sei');
const seiProcesso = screen('/sei/$seiId', () => import('./screens/SeiProcesso.tsx'));
// Consulta keeps its state in the URL (shareable): the query, then a chosen holder or process.
export const ConsultaSearch = z.object({
  q: z.string().trim().min(1).max(120).optional().catch(undefined),
  pessoa: PublicId.optional().catch(undefined),
  processo: PublicId.optional().catch(undefined),
});
export type ConsultaSearch = z.infer<typeof ConsultaSearch>;
const consulta = createRoute({
  getParentRoute: () => rootRoute,
  path: '/consulta',
  // `?q=868092` or ids arrive as numbers (JSON search values) — keep them strings, like the API's ids
  validateSearch: (raw: Record<string, unknown>) => {
    const str = (v: unknown) => (v == null ? undefined : String(v));
    return ConsultaSearch.parse({ q: str(raw.q), pessoa: str(raw.pessoa), processo: str(raw.processo) });
  },
  component: lazyRouteComponent(() => import('./screens/Consulta.tsx')),
});
// Account (Plan 4a, S-x): `?para=` = where to go after signing in / up (a path on this site; checked again on use)
const Para = (raw: Record<string, unknown>) => z.object({ para: z.string().max(500).optional().catch(undefined) }).parse({ para: asString(raw.para) });
const withPara = <P extends string>(path: P, load: () => Promise<{ default: ComponentType }>) =>
  createRoute({ getParentRoute: () => rootRoute, path, validateSearch: Para, component: lazyRouteComponent(load) });
const entrar = withPara('/entrar', () => import('./screens/Entrar.tsx'));
const esqueci = screen('/entrar/esqueci', () => import('./screens/Esqueci.tsx'));
const cadastro = withPara('/cadastro', () => import('./screens/Cadastro.tsx'));
const verificar = withPara('/cadastro/verificar', () => import('./screens/CadastroVerificar.tsx'));
const quemPaga = withPara('/comecar/quem-paga', () => import('./screens/QuemPaga.tsx'));
const seguranca = screen('/conta/seguranca', () => import('./screens/Seguranca.tsx'));
const carteira = screen('/carteira', () => import('./screens/Carteira.tsx'));
const sobreposicao = moved('/sobreposicao', '/territorio', 'sobreposicao');
const oportunidades = moved('/oportunidades', '/territorio', 'oportunidades');
const superficiarios = moved('/superficiarios', '/territorio', 'superficiarios');
// Onboarding keeps its query in the URL like the Consulta (`?q=`, kept a string); Planos takes the AnmEntity chosen there.
const onboarding = createRoute({
  getParentRoute: () => rootRoute,
  path: '/comecar',
  validateSearch: (raw: Record<string, unknown>) => z.object({ q: z.string().trim().min(1).max(120).optional().catch(undefined) }).parse({ q: asString(raw.q) }),
  component: lazyRouteComponent(() => import('./screens/Onboarding.tsx')),
});
const planos = createRoute({
  getParentRoute: () => rootRoute,
  path: '/planos',
  validateSearch: (raw: Record<string, unknown>) => z.object({ anmEntity: PublicId.optional().catch(undefined) }).parse({ anmEntity: asString(raw.anmEntity) }),
  component: lazyRouteComponent(() => import('./screens/Planos.tsx')),
});

// Cobrança (Plan 4a Task 5): the billing page — all an inadimplente customer can reach (S3)
const cobranca = screen('/cobranca', () => import('./screens/Cobranca.tsx'));

// Status: every data source's state, schedule and data date (replaces the rail's "Última sincronização")
const status = screen('/status', () => import('./screens/Status.tsx'));

// Alertas (Plan 4c): the login's preferences, and the public unsubscribe page an alert e-mail links to (`?t=` token)
const alertas = screen('/alertas', () => import('./screens/Alertas.tsx'));
const descadastrar = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alertas/descadastrar',
  validateSearch: (raw: Record<string, unknown>) => z.object({ t: z.string().min(10).max(1000).optional().catch(undefined) }).parse({ t: asString(raw.t) }),
  component: lazyRouteComponent(() => import('./screens/Descadastrar.tsx')),
});

// Consultor & parceiro, Equipe and the invite landing (Plan 4a Tasks 6–7); `/parceiro` is where the partner e-mails link
const parceiro = screen('/parceiro', () => import('./screens/Parceiro.tsx'));
const equipe = screen('/equipe', () => import('./screens/Equipe.tsx'));
const convite = screen('/convite/$token', () => import('./screens/Convite.tsx'));

export const routeTree = rootRoute.addChildren([
  atividades, prazos, processos, processo, territorio, sei, seiProcesso, consulta, entrar, esqueci, cadastro, verificar, carteira, sobreposicao, oportunidades, superficiarios, onboarding,
  quemPaga, planos, cobranca, status, alertas, descadastrar, seguranca,
  parceiro, equipe, convite,
]);

export const makeRouter = (history?: RouterHistory) =>
  createRouter({ routeTree, defaultPreload: 'intent', parseSearch, stringifySearch, ...(history ? { history } : {}) });

/**
 * Plain search params: `?processo=303763`, not the router's default JSON (`?processo=%22303763%22`) — quotes in URLs
 * break shared links and look like injection to firewalls. Values are strings (the routes validate them); a legacy
 * quoted value (`"303763"`) is still read.
 */
export const searchValue = (s: string): unknown => {
  if (s.length >= 2 && s.startsWith('"') && s.endsWith('"')) {
    try {
      return JSON.parse(s) as unknown;
    } catch {
      return s;
    }
  }
  return s;
};
const parseRaw = parseSearchWith(searchValue);
// the query-string decoder turns "868092" into the number 868092 before our parser runs: back to text, always
const parseSearch = (search: string): Record<string, unknown> =>
  Object.fromEntries(Object.entries(parseRaw(search)).map(([k, v]) => [k, typeof v === 'number' || typeof v === 'boolean' ? String(v) : v]));
const stringifySearch = stringifySearchWith((v: unknown) => (typeof v === 'string' ? v : JSON.stringify(v)), searchValue);

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof makeRouter>;
  }
}
